import { isAdmin } from '../../../helpers/roleHelpers';
import { uniqueId } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Plus, X, ArrowsMove, BoxArrowInDown } from 'react-bootstrap-icons';
import useFileUpload from 'react-use-file-upload';
import { makeApiRequests } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import { getImageUrl } from '../../../helpers/imageHelpers';
import { toast } from 'react-toastify';
import CircularProgressBar from '../circular-progress';

const ImagePreview = ({
  image,
  onRemove,
  onClick,
  showDelete = true,
  isDraggable = false,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragOver = false,
  isDragging = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [dragStarted, setDragStarted] = useState(false);

  const handleMouseDown = () => {
    if (isDraggable) {
      setDragStarted(false);
    }
  };

  const handleDragStart = e => {
    setDragStarted(true);
    onDragStart && onDragStart(e);
  };

  const handleClick = e => {
    if (!dragStarted && onClick) {
      onClick(e);
    }
    setDragStarted(false);
  };

  return (
    <div
      className="position-relative d-inline-block m-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      style={{
        cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        userSelect: 'none'
      }}
      draggable={isDraggable}
      onDragStart={handleDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {isDraggable && (
        <div
          style={{
            position: 'absolute',
            top: '1px',
            left: '1px',
            zIndex: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '4px',
            padding: '2px',
            cursor: 'grab',
            opacity: isHovered ? 1 : 0.7,
            transition: 'opacity 0.2s ease',
            pointerEvents: 'none',
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}
        >
          <ArrowsMove color="#007bff" size={18} />
        </div>
      )}

      <div
        style={{
          width: '120px',
          height: '120px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: isDragOver ? '3px solid #007bff' : isDragging ? '3px solid #28a745' : '2px solid #e0e0e0',
          position: 'relative',
          opacity: isDragging ? 0.5 : 1,
          transform: isDragging ? 'rotate(2deg) scale(0.95)' : isDragOver ? 'scale(1.05)' : 'none',
          transition: 'all 0.2s ease',
          backgroundColor: isDragOver
            ? 'rgba(0, 123, 255, 0.3)'
            : isDragging
            ? 'rgba(40, 167, 69, 0.1)'
            : 'transparent',
          cursor: isDraggable ? 'inherit' : 'pointer',
          pointerEvents: isDragging ? 'none' : 'auto'
        }}
      >
        <img
          src={getImageUrl(image)}
          alt="Preview"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            pointerEvents: isDraggable ? 'none' : 'auto',
            userSelect: 'none'
          }}
          draggable={false}
        />

        {/* Status indicator */}
        {image.status === 'WAITING_FOR_UPLOAD' && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              padding: '8px'
            }}
          >
            <CircularProgressBar size={1} />
          </div>
        )}

        {image.status === 'ERROR' && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(255, 0, 0, 0.8)',
              color: 'white',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '12px'
            }}
          >
            Error
          </div>
        )}

        {/* Drag overlay when being dragged */}
        {isDragging && (
          <div
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: 'rgba(0, 123, 255, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              border: '2px dashed white'
            }}
          >
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '50%',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }}
            >
              <ArrowsMove color="#007bff" size={32} />
            </div>
          </div>
        )}

        {/* Drop indicator when another image is being dragged over */}
        {isDragOver && (
          <div
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              zIndex: 15
            }}
          >
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '50%',
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                border: '2px solid #007bff'
              }}
            >
              <BoxArrowInDown color="#007bff" size={28} />
            </div>
          </div>
        )}
      </div>

      {/* Hover delete button */}
      {isHovered && showDelete && (
        <button type="button" aria-label="Remove photo"
          onClick={e => {
            e.stopPropagation();
            onRemove();
          }}
          style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            width: '24px',
            height: '24px',
            backgroundColor: '#dc3545',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          <X color="white" size={14} />
        </button>
      )}
    </div>
  );
};

const ImageUploader = ({ onClick }) => (
  <div className="position-relative d-inline-block m-2" onClick={onClick} style={{ cursor: 'pointer' }}>
    <div
      style={{
        width: '120px',
        height: '120px',
        borderRadius: '8px',
        border: '2px dashed #28a745',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
        transition: 'all 0.2s ease'
      }}
      className="hover-bg-light"
    >
      <Plus color="#28a745" size={32} />
      <span style={{ color: '#28a745', fontSize: '14px', marginTop: '8px' }}>Add Image</span>
    </div>
  </div>
);

const Image = ({
  uploadedImages = [],
  onUploadedImagesChange,
  existingImages = [],
  onExistingImageDelete,
  onImageReorder,
  images = [],
  onImagesChange,
  isEditMode = true,
  multiple = true,
  containerClassName = '',
  showTitle = true,
  title = 'Uploaded Images'
}) => {
  const { files, handleDragDropEvent, setFiles } = useFileUpload();
  const inputRef = useRef();
  const uploadBatchRef = useRef({ total: 0, completed: 0, failed: 0 });
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const isUnifiedMode = onImagesChange !== undefined;
  const allImages = isUnifiedMode ? images : [...existingImages, ...uploadedImages];

  useEffect(() => {
    const newImages = [];
    for (const file of files) {
      const previewUrl = URL.createObjectURL(file);
      const imageToBeUploaded = {
        file,
        status: 'WAITING_FOR_UPLOAD',
        id: uniqueId(),
        previewUrl,
        filename: file.name,
        name: file.name
      };
      newImages.push(imageToBeUploaded);
    }

    if (newImages.length > 0) {
      // Initialize batch tracking
      uploadBatchRef.current = { total: newImages.length, completed: 0, failed: 0 };

      if (isUnifiedMode) {
        const updatedImages = multiple ? [...allImages, ...newImages] : newImages;
        onImagesChange && onImagesChange(updatedImages);
      } else {
        const updatedImages = multiple ? [...uploadedImages, ...newImages] : newImages;
        onUploadedImagesChange && onUploadedImagesChange(updatedImages);
      }
    }
  }, [files]);

  useEffect(() => {
    const uploadImages = async () => {
      const imagesToUpload = allImages.filter(img => img.status === 'WAITING_FOR_UPLOAD');

      if (imagesToUpload.length > 0) {
        for (const image of imagesToUpload) {
          // processing image one by one.
          await uploadImage(image);
        }
      }
    };
    uploadImages();
  }, [allImages.length]);

  const uploadImage = async imageToBeUploaded => {
    try {
      const formData = new FormData();
      formData.append('image', imageToBeUploaded.file);

      const { error, response } = await makeApiRequests({
        requestBody: formData,
        endpoint: ENDPOINTS.DISK_IMAGE_UPLOAD,
        method: 'POST',
        stringify: false
      });

      if (error) {
        updateUploadedImages(imageToBeUploaded, 'ERROR');
        uploadBatchRef.current.failed++;
      } else {
        updateUploadedImages(imageToBeUploaded, 'UPLOADED', response);
        uploadBatchRef.current.completed++;
      }
    } catch (error) {
      updateUploadedImages(imageToBeUploaded, 'ERROR');
      uploadBatchRef.current.failed++;
    }

    const { total, completed, failed } = uploadBatchRef.current;
    if (completed + failed === total && total > 0) {
      if (failed === 0) {
        toast.success(`${total} image${total > 1 ? 's' : ''} uploaded successfully`);
      } else if (completed === 0) {
        toast.error(`Failed to upload ${total} image${total > 1 ? 's' : ''}`);
      } else {
        toast.warning(`${completed} uploaded successfully, ${failed} failed`);
      }
      uploadBatchRef.current = { total: 0, completed: 0, failed: 0 };
    }
  };

  const updateUploadedImages = (imageObject, status, response = {}) => {
    const updateImageList = images =>
      images.map(img => (img.id === imageObject.id ? { ...img, ...response, status } : img));

    if (isUnifiedMode) {
      onImagesChange &&
        onImagesChange(prevImages => updateImageList(Array.isArray(prevImages) ? prevImages : allImages));
    } else {
      onUploadedImagesChange &&
        onUploadedImagesChange(prevImages => updateImageList(Array.isArray(prevImages) ? prevImages : uploadedImages));
    }
  };

  const onRemoveImage = imageObject => {
    if (isAdmin() && imageObject.status === 'UPLOADED' && imageObject.filename) {
      makeApiRequests({
        endpoint: ENDPOINTS.FILE_WITH_ID(imageObject.filename),
        method: 'DELETE'
      });
    }
    if (imageObject.previewUrl && imageObject.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageObject.previewUrl);
    }

    if (isUnifiedMode) {
      const updatedImages = allImages.filter(img => img.id !== imageObject.id);
      onImagesChange && onImagesChange(updatedImages);
    } else {
      const updatedImages = uploadedImages.filter(img => img.id !== imageObject.id);
      onUploadedImagesChange && onUploadedImagesChange(updatedImages);
    }
  };

  const validateFiles = files => {
    const fileArray = Array.from(files);
    const invalidFiles = fileArray.filter(file => !file.type.startsWith('image/'));

    if (invalidFiles.length > 0) {
      toast.error(`Only image files are allowed. Please select valid image files.`);
      return false;
    }
    return true;
  };

  const handleFileSelect = () => {
    inputRef.current.click();
  };

  const handleImageClick = image => {
    setPreviewImage(image);
    setShowPreview(true);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
    e.target.style.opacity = '0.5';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === targetIndex) {
      resetDragState();
      return;
    }

    reorderImages(draggedIndex, targetIndex);
    resetDragState();
  };

  const handleDragEnd = () => {
    resetDragState();
  };

  const resetDragState = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const reorderImages = (fromIndex, toIndex) => {
    if (isUnifiedMode) {
      const newImages = [...allImages];
      const [movedImage] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, movedImage);
      onImagesChange && onImagesChange(newImages);
    } else {
      const fromActualIndex = fromIndex;
      const toActualIndex = toIndex;
      const newAllImages = [...allImages];
      const [movedImage] = newAllImages.splice(fromActualIndex, 1);
      newAllImages.splice(toActualIndex, 0, movedImage);
      const newExistingImages = [];
      const newUploadedImages = [];

      newAllImages.forEach(image => {
        if (image.hasOwnProperty('file') || image.hasOwnProperty('status')) {
          newUploadedImages.push(image);
        } else {
          newExistingImages.push(image);
        }
      });
      if (onImageReorder) {
        onImageReorder(newExistingImages, newUploadedImages);
      }
      if (
        newUploadedImages.length !== uploadedImages.length ||
        !newUploadedImages.every((img, i) => img === uploadedImages[i])
      ) {
        onUploadedImagesChange && onUploadedImagesChange(newUploadedImages);
      }
    }
  };

  return (
    <div className={`mb-3 ${containerClassName}`}>
      {showTitle && <h6 className="midFont mb-3">{title}</h6>}
      {allImages.length === 0 && !isEditMode && <p className="text-muted mb-3">No Images Uploaded Yet.</p>}

      <div
        className="d-flex flex-wrap align-items-start"
        onDragEnter={handleDragDropEvent}
        onDragOver={handleDragDropEvent}
        onDrop={e => {
          e.preventDefault();
          if (validateFiles(e.dataTransfer.files)) {
            handleDragDropEvent(e);
            setFiles(e);
          }
        }}
      >
        {allImages.map((image, index) => {
          const imageKey = isUnifiedMode
            ? `unified-${index}-${image.filename || image.id}`
            : index < existingImages.length
            ? `existing-${index}`
            : image.id;

          return (
            <ImagePreview
              key={imageKey}
              image={image}
              onRemove={() => {
                if ((image.isExisting || (!image.status && (image.url || image.filename))) && !isAdmin()) return;
                if (isUnifiedMode) {
                  const newImages = allImages.filter((_, i) => i !== index);
                  onImagesChange && onImagesChange(newImages);
                } else {
                  const isExisting = index < existingImages.length;
                  const adjustedIndex = isExisting ? index : index - existingImages.length;

                  if (isExisting && onExistingImageDelete) {
                    onExistingImageDelete(image, adjustedIndex);
                  } else if (!isExisting) {
                    onRemoveImage(image);
                  }
                }
              }}
              onClick={() => handleImageClick(image)}
              showDelete={isEditMode && (isAdmin() || !(image.isExisting || (!image.status && (image.url || image.filename))))}
              isDraggable={isEditMode}
              onDragStart={e => handleDragStart(e, index)}
              onDragOver={e => handleDragOver(e, index)}
              onDrop={e => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              isDragOver={dragOverIndex === index}
              isDragging={draggedIndex === index}
            />
          );
        })}
        {isEditMode && <ImageUploader onClick={handleFileSelect} />}
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept="image/*"
        style={{ display: 'none' }}
        onChange={e => {
          if (validateFiles(e.target.files)) {
            setFiles(e);
            e.target.value = '';
          }
        }}
      />

      {/* Image Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-0">
          {previewImage && (
            <img
              src={getImageUrl(previewImage)}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Image;
